import {
  AUTH_LOGOUT_INTENT_KEY,
  AUTH_SESSION_STORAGE_KEY,
  AUTH_TOKEN_STORAGE_KEY,
  OFFLINE_USER_PROFILE_KEY,
  OFFLINE_AUTHORIZATION_CONTEXT_KEY,
} from '../../auth-core/constants/auth.constants';
import type { AuthenticationSession } from '../../auth-core/models/auth-session.model';
import type { AuthTokenEnvelope } from '../../auth-core/models/auth-token-envelope.model';
import type { UserProfile } from '../../auth-core/models/user-profile.model';
import { SESSION_CORRELATION_ID_KEY } from '../../../http/correlation.interceptor';

const AUTH_STATE_MISMATCH_RECOVERY_KEY = 'AUTH_STATE_MISMATCH_RECOVERY';
const AUTH_INTERACTION_LOCK_KEY = 'AUTH_INTERACTION_LOCK';
const AUTH_INTERACTION_LOCK_TIMEOUT_MS = 5000; // 5 seconds max lock duration

export function saveOfflineUserProfile(profile: UserProfile): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(OFFLINE_USER_PROFILE_KEY, JSON.stringify(profile));
      if (profile.userId) {
        localStorage.setItem('symphony.offline.lastUserId', profile.userId);
      }
    }
  } catch {
    // ignore
  }
}

export function loadOfflineUserProfile(): UserProfile | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(OFFLINE_USER_PROFILE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as UserProfile;
  } catch {
    return null;
  }
}

export function clearOfflineUserProfile(): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(OFFLINE_USER_PROFILE_KEY);
    }
  } catch {
    // ignore
  }
}

/**
 * Last known JWT-derived roles/permissions/claims, cached so an offline PIN-restored session (which has no JWT to
 * decode) can still resolve authorization instead of falling back to an empty context.
 */
export interface OfflineAuthorizationContext {
  roles: string[];
  permissions: string[];
  claims: Record<string, unknown>;
}

export function saveOfflineAuthorizationContext(context: OfflineAuthorizationContext): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(OFFLINE_AUTHORIZATION_CONTEXT_KEY, JSON.stringify(context));
    }
  } catch {
    // ignore
  }
}

export function loadOfflineAuthorizationContext(): OfflineAuthorizationContext | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(OFFLINE_AUTHORIZATION_CONTEXT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as OfflineAuthorizationContext;
  } catch {
    return null;
  }
}

export function clearOfflineAuthorizationContext(): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(OFFLINE_AUTHORIZATION_CONTEXT_KEY);
    }
  } catch {
    // ignore
  }
}

export function saveSession(session: AuthenticationSession): void {
  sessionStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(session));
  if (
    session.userProfile &&
    (session.userProfile.userName || session.userProfile.userId || session.userProfile.userEmail)
  ) {
    saveOfflineUserProfile(session.userProfile);
  }
}

export function loadSession(): AuthenticationSession | null {
  const raw = sessionStorage.getItem(AUTH_SESSION_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthenticationSession;
  } catch {
    sessionStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
    return null;
  }
}

export function clearSession(): void {
  sessionStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
  // Start a fresh correlation ID for the next login so tracing doesn't span two different users' sessions.
  sessionStorage.removeItem(SESSION_CORRELATION_ID_KEY);
}

/** Shell-internal only — tokens must never be read directly by remotes. */
export function saveToken(envelope: AuthTokenEnvelope): void {
  sessionStorage.setItem(AUTH_TOKEN_STORAGE_KEY, JSON.stringify(envelope));
}

/** Shell-internal only — returns null when no token is cached. */
export function loadToken(): AuthTokenEnvelope | null {
  const raw = sessionStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthTokenEnvelope;
  } catch {
    sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    return null;
  }
}

export function clearToken(): void {
  sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
}

/** Marks that the current tab initiated an explicit logout and should not auto-login. */
export function markLogoutIntent(): void {
  sessionStorage.setItem(AUTH_LOGOUT_INTENT_KEY, '1');
}

/** Clears explicit logout intent so login automation can resume. */
export function clearLogoutIntent(): void {
  sessionStorage.removeItem(AUTH_LOGOUT_INTENT_KEY);
}

/** Returns true when explicit logout intent is currently active for this tab. */
export function hasLogoutIntent(): boolean {
  return sessionStorage.getItem(AUTH_LOGOUT_INTENT_KEY) === '1';
}

/** Marks that state_mismatch recovery was attempted to prevent login loops. */
export function markStateMismatchRecovery(): void {
  sessionStorage.setItem(AUTH_STATE_MISMATCH_RECOVERY_KEY, '1');
}

/** Clears state_mismatch recovery flag. */
export function clearStateMismatchRecovery(): void {
  sessionStorage.removeItem(AUTH_STATE_MISMATCH_RECOVERY_KEY);
}

/** Returns true if state_mismatch recovery was already attempted (prevents login loop). */
export function hasStateMismatchRecoveryAttempted(): boolean {
  return sessionStorage.getItem(AUTH_STATE_MISMATCH_RECOVERY_KEY) === '1';
}

/**
 * Attempts to acquire the MSAL interaction lock for this tab. Returns true if lock was acquired, false if another tab
 * holds the lock. Lock automatically expires after AUTH_INTERACTION_LOCK_TIMEOUT_MS.
 */
export function tryAcquireInteractionLock(): boolean {
  const now = Date.now();
  const lockData = sessionStorage.getItem(AUTH_INTERACTION_LOCK_KEY);

  // Check if lock exists and is still valid (not expired)
  if (lockData) {
    try {
      const lockTime = parseInt(lockData, 10);
      if (now - lockTime < AUTH_INTERACTION_LOCK_TIMEOUT_MS) {
        return false; // Lock held by another tab
      }
    } catch {
      // Continue if lock data is invalid
    }
  }

  // Acquire lock
  sessionStorage.setItem(AUTH_INTERACTION_LOCK_KEY, now.toString());
  return true;
}

/** Releases the MSAL interaction lock. */
export function releaseInteractionLock(): void {
  sessionStorage.removeItem(AUTH_INTERACTION_LOCK_KEY);
}

/**
 * Waits for the interaction lock to be released by other tabs. Returns a promise that resolves when lock is released or
 * timeout expires. Timeout defaults to AUTH_INTERACTION_LOCK_TIMEOUT_MS.
 */
export async function waitForInteractionLockRelease(
  timeoutMs: number = AUTH_INTERACTION_LOCK_TIMEOUT_MS
): Promise<void> {
  const startTime = Date.now();
  const pollIntervalMs = 50; // Poll every 50ms

  return new Promise((resolve) => {
    const pollLock = () => {
      const lockData = sessionStorage.getItem(AUTH_INTERACTION_LOCK_KEY);
      const elapsed = Date.now() - startTime;

      // Lock released or timeout
      if (!lockData || elapsed >= timeoutMs) {
        resolve();
        return;
      }

      // Check if lock is expired
      try {
        const lockTime = parseInt(lockData, 10);
        if (Date.now() - lockTime >= AUTH_INTERACTION_LOCK_TIMEOUT_MS) {
          resolve();
          return;
        }
      } catch {
        resolve();
        return;
      }

      // Continue polling
      setTimeout(pollLock, pollIntervalMs);
    };

    pollLock();
  });
}
